import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as countriesApi from './countries.api';

import type { CountryListQuery, CreateCountryRequest, UpdateCountryRequest } from '@utazzo/shared';

const QK = {
  list: (query: CountryListQuery) => ['countries', 'list', query] as const,
  detail: (id: string) => ['countries', 'detail', id] as const,
};

export const useCountriesList = (query: CountryListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => countriesApi.listCountries(query),
    staleTime: 30_000,
  });

export const useCreateCountry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCountryRequest) => countriesApi.createCountry(payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['countries', 'list'] });
      toast.success(`Added ${c.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add country'),
  });
};

export const useUpdateCountry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCountryRequest }) =>
      countriesApi.updateCountry(id, payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['countries', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(c.id) });
      toast.success('Country updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update country'),
  });
};

export const useDeleteCountry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => countriesApi.deleteCountry(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['countries', 'list'] });
      toast.success('Country deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete country'),
  });
};

export const useBulkDeleteCountries = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => countriesApi.bulkDeleteCountries(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['countries', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete countries'),
  });
};

export const useBulkSetCountriesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      countriesApi.bulkSetCountriesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['countries', 'list'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update country status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
